// YNX PWA Generator modal — one-click installable mobile app builder.
import React, { useState } from 'react';
import { X, Smartphone, Download, Sparkles } from 'lucide-react';
import { generatePwa, downloadPwaZip } from '../lib/pwa';
import { useToast } from './Toast';

export const PwaGenModal = ({ open, onClose }) => {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [features, setFeatures] = useState('');
  const [themeColor, setThemeColor] = useState('#00ffff');
  const [backgroundColor, setBackgroundColor] = useState('#0a0a0f');
  const [iconPrompt, setIconPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');

  if (!open) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleGenerate = async () => {
    if (!name.trim() || !description.trim()) {
      toast('Donne un nom et une description', 'error');
      return;
    }
    setBusy(true);
    setStep('Génération de l\'icône (Nano Banana)…');
    try {
      const payload = await generatePwa({
        name: name.trim(),
        description: description.trim(),
        features: features.trim(),
        themeColor,
        backgroundColor,
        iconPrompt: iconPrompt.trim(),
      });
      setStep('Empaquetage du .zip…');
      await downloadPwaZip(payload);
      toast('PWA générée ! Glisse le .zip sur Netlify Drop pour l\'héberger 🚀', 'success');
      setStep('');
      setBusy(false);
      onClose();
    } catch (e) {
      console.error(e);
      toast(`Erreur PWA : ${e.message}`, 'error');
      setBusy(false);
      setStep('');
    }
  };

  return (
    <div
      className='modal-overlay active'
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      data-testid='pwa-modal-overlay'
    >
      <div className='modal' role='dialog' aria-modal='true'>
        <div className='modal-header'>
          <div className='modal-title' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Smartphone size={16} />
            <span>PWA · APP INSTALLABLE</span>
          </div>
          <button className='modal-close' onClick={handleClose} disabled={busy} data-testid='pwa-modal-close'>
            <X size={14} />
          </button>
        </div>

        <div className='modal-body'>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 18, lineHeight: 1.6 }}>
            Décris l&apos;app, YNX génère un <b>.zip prêt à déployer</b> (Netlify Drop / Vercel / GitHub Pages).
            Installable sur <b>Android</b> + <b>iOS</b> en 1 clic depuis le navigateur.
          </p>

          <div className='modal-section'>
            <label className='modal-label'>NOM DE L&apos;APP</label>
            <input
              type='text'
              name='pwa-name'
              className='hud-input'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Ma Super App'
              maxLength={60}
              disabled={busy}
              data-testid='pwa-name-input'
            />
          </div>

          <div className='modal-section'>
            <label className='modal-label'>DESCRIPTION</label>
            <textarea
              name='pwa-desc'
              className='hud-input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Une app de gestion de tâches avec catégories, drag&drop, dark mode et stats hebdo."
              rows={3}
              maxLength={400}
              disabled={busy}
              data-testid='pwa-desc-input'
            />
          </div>

          <div className='modal-section'>
            <label className='modal-label'>FONCTIONNALITÉS (optionnel)</label>
            <textarea
              name='pwa-features'
              className='hud-input'
              value={features}
              onChange={(e) => setFeatures(e.target.value)}
              placeholder={'- Ajouter/supprimer/marquer terminé\n- Filtres par tag\n- Export JSON'}
              rows={3}
              maxLength={600}
              disabled={busy}
              data-testid='pwa-features-input'
            />
          </div>

          <div className='modal-section' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label className='modal-label'>COULEUR ACCENT</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type='color'
                  name='pwa-theme-color'
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  disabled={busy}
                  style={{ width: 42, height: 32, border: '1px solid var(--wire)', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                  data-testid='pwa-theme-color'
                />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)' }}>{themeColor}</span>
              </div>
            </div>
            <div>
              <label className='modal-label'>FOND</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type='color'
                  name='pwa-bg-color'
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  disabled={busy}
                  style={{ width: 42, height: 32, border: '1px solid var(--wire)', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                  data-testid='pwa-bg-color'
                />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)' }}>{backgroundColor}</span>
              </div>
            </div>
          </div>

          <div className='modal-section'>
            <label className='modal-label'>PROMPT ICÔNE (optionnel)</label>
            <input
              type='text'
              name='pwa-icon-prompt'
              className='hud-input'
              value={iconPrompt}
              onChange={(e) => setIconPrompt(e.target.value)}
              placeholder='Logo minimaliste, coche cyan sur fond noir (sinon YNX choisit)'
              disabled={busy}
              data-testid='pwa-icon-prompt'
            />
          </div>

          {busy && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--cyan)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <Sparkles size={12} className='spin' /> {step}
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <button className='hud-btn' onClick={handleClose} disabled={busy} data-testid='pwa-cancel'>
            ANNULER
          </button>
          <button
            className='hud-btn primary'
            onClick={handleGenerate}
            disabled={busy || !name.trim() || !description.trim()}
            data-testid='pwa-generate-btn'
          >
            {busy ? (
              <>
                <Sparkles size={14} className='spin' /> GÉNÉRATION…
              </>
            ) : (
              <>
                <Download size={14} /> GÉNÉRER LE .ZIP
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
